import {useMutation, useQuery} from '@tanstack/react-query';
import axiosClient from '../services/axiosClient';

/**
 * Get all assessments with pagination
 */
export const useGetAssessmentsQuery = (params) => {
  return useQuery(['assessments', params], async () => {
    const {data} = await axiosClient.get('/assessments', {
      params,
    });
    return data;
  });
};

/**
 * Get assessment by ID
 */
export const useGetAssessmentByIdQuery = (id, options = {}) => {
  return useQuery(
    ['assessment', id],
    async () => {
      const {data} = await axiosClient.get(`/assessments/${id}`);
      return data;
    },
    {
      enabled: !!id, // Only run when id exists
      ...options,
    },
  );
};

/**
 * Get candidates by assessment ID
 */
export const useGetAssessmentCandidatesQuery = (assessmentId, params) => {
  return useQuery(['assessmentCandidates', assessmentId, params], async () => {
    const {data} = await axiosClient.get(
      `/assessment-candidates/assessment/${assessmentId}`,
      {
        params,
      },
    );
    return data;
  });
};

/**
 * Create new assessment
 */
export const useCreateAssessmentMutation = () =>
  useMutation(async (payload) => {
    const {data} = await axiosClient.post('/assessments', payload);
    return data;
  });

/**
 * Update assessment
 */
export const useUpdateAssessmentMutation = () =>
  useMutation(async ({id, ...payload}) => {
    const {data} = await axiosClient.put(`/assessments/${id}`, payload);
    return data;
  });

/**
 * Delete assessment
 */
export const useDeleteAssessmentMutation = () =>
  useMutation(async (id) => {
    const {data} = await axiosClient.delete(`/assessments/${id}`);
    return data;
  });

/**
 * Get assessment test results by candidate ID
 */
export const useGetAssessmentTestResultsQuery = (assessmentId, candidateId) => {
  return useQuery(
    ['assessmentTestResults', assessmentId, candidateId],
    async () => {
      const {data} = await axiosClient.get(
        `/assessment-test-sessions/${assessmentId}/candidate/${candidateId}/results`,
      );
      return data;
    },
  );
};

/**
 * Update resultPublished flag for assessment sessions
 */
export const useUpdateResultPublishedMutation = () =>
  useMutation(async ({assessmentId, resultPublished}) => {
    const {data} = await axiosClient.put(
      `/assessments/${assessmentId}/publish-results`,
      {
        resultPublished,
      },
    );
    return data;
  });

export const useExportAssessmentsQuery = (assessmentId, params) => {
  return useQuery(['assessmentCandidates', assessmentId, params], async () => {
    const {data} = await axiosClient.get(
      `/assessment-candidates/assessment/${assessmentId}`,
      {
        params: {...(params ?? {}), is_export: true},
      },
    );
    return data;
  });
};

/**
 * Get users by role (for fetching invigilators)
 */
export const useGetUsersByRoleQuery = (
  role,
  assessmentId = null,
  options = {},
) => {
  return useQuery(
    ['users', role, assessmentId],
    async () => {
      const params = {
        role,
        limit: 999999, // Very high limit to get all users without pagination
        page: 1,
      };

      // Add assessmentId to params if provided
      if (assessmentId) {
        params.assessmentId = assessmentId;
      }

      const {data} = await axiosClient.get('/account/users', {
        params,
      });
      return data;
    },
    {
      enabled: !!role,
      ...options,
    },
  );
};
